import {
  PlaidLinkStableEvent,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
} from 'react-plaid-link';
import { WidgetEventPayloadType } from 'aerosync-web-sdk';
import { LDClient } from 'launchdarkly-js-client-sdk';

import { ReceiptDiscountSummary } from '@dutchie/common/utils/receipt-summary';
import {
  PBB_IDENTITY_CHANGED_FIELDS,
  PBB_UNENROLLMENT_REASONS,
  PBB_UNENROLLMENT_SURFACES,
} from 'shared/constants/dutchie-pay';
import { ProductsProvider } from 'src/gql/graphql';
import type { Products, Orders, Dispensaries, Order_Product as OrderProduct, OrdersCreateInput } from 'src/gql/graphql';
import { Product } from 'types/graphql-overrides';
import { BankConnectionContexts, EnrollmentContexts, EVENT_ACTIONS } from 'src/payments/hooks';
import {
  AeroSyncPreAuthEvent,
  AeroSyncPreAuthEventMetadata,
  CheckoutFlow,
  EnrollmentSteps,
  SignupButtonLocation,
} from 'src/payments/constants';
import type { ConciergeTelemetryEvents } from 'src/concierge/contract';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { ProductPlacement } from 'src/types';
import { Integrations } from 'src/components/loyalty/use-loyalty/types';
import type { NudgeLeg } from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import { AnalyticsFeatureFlags } from './monitors/use-feature-flags-monitor';

/**
 * Type for instore enrollment steps used in tracking events
 */
export type InstoreEnrollmentStep =
  | 'connectBank'
  | 'login'
  | 'loyalty'
  | 'medIntake'
  | 'medIntakeSuccess'
  | 'payByBankIntro'
  | 'success';

/**
 * Names of the onboarding modals used in tracking events.
 * Single source of truth for the TS union and the yup schema `oneOf`.
 */
export const ONBOARDING_MODAL_NAMES = ['bankLink', 'loyaltyJoin', 'loyaltyPass', 'phoneVerify'] as const;
export type OnboardingModalName = typeof ONBOARDING_MODAL_NAMES[number];

/**
 * Actions on the onboarding modals used in tracking events.
 */
export const ONBOARDING_MODAL_ACTIONS = ['completed', 'declined', 'dismissed', 'errored', 'opened'] as const;
export type OnboardingModalAction = typeof ONBOARDING_MODAL_ACTIONS[number];

/**
 * Entry points that can start the post-signup onboarding sequence. Adding a new surface
 * here is a deliberate change so the analyst tracking these events can be told about it.
 */
export const ONBOARDING_MODAL_SOURCES = ['postSignup'] as const;
export type OnboardingModalSource = typeof ONBOARDING_MODAL_SOURCES[number];

export const STORE_ROUTING_ACTIONS = ['viewed', 'accepted', 'declined', 'failed'] as const;
type StoreRoutingAction = typeof STORE_ROUTING_ACTIONS[number];

export const STORE_ROUTING_SOURCES = [
  'checkout_order_type',
  'delivery_address_form',
  'menu_home',
  'add_to_cart_validation',
  'chain_locator',
] as const;
export type StoreRoutingSource = typeof STORE_ROUTING_SOURCES[number];

export const STORE_ROUTING_FAILURE_REASONS = ['lookup', 'navigation'] as const;
type StoreRoutingFailureReason = typeof STORE_ROUTING_FAILURE_REASONS[number];

// Checkout-flow actions surrounding the Pay-by-Bank decision (Q7 funnel context). Non-PII enum values only.
export const CHECKOUT_FLOW_ACTIONS = ['fulfillment_selected', 'payment_mode_selected', 'rewards_action'] as const;
export type CheckoutFlowActionType = typeof CHECKOUT_FLOW_ACTIONS[number];

export { PBB_IDENTITY_CHANGED_FIELDS, PBB_UNENROLLMENT_REASONS, PBB_UNENROLLMENT_SURFACES };
export type PbbIdentityChangedField = typeof PBB_IDENTITY_CHANGED_FIELDS[number];
export type PbbUnenrollmentReason = typeof PBB_UNENROLLMENT_REASONS[number];
export type PbbUnenrollmentSurface = typeof PBB_UNENROLLMENT_SURFACES[number];

/**
 * Education sections on the redesigned PBB signup page (carousel slides on desktop,
 * stacked sections on mobile). Slugs are the analytics contract — rename only with
 * an analyst heads-up.
 */
export const PBB_EDUCATION_SECTIONS = ['how_it_works', 'benefits', 'faq'] as const;
export type PbbEducationSection = typeof PBB_EDUCATION_SECTIONS[number];

/**
 * Which viewport layout rendered the education content, from the md (900px) media-query
 * branch in the signup layout. Named viewport, not surface: `surface` elsewhere in this
 * file means entry point/placement.
 */
export const PBB_EDUCATION_VIEWPORTS = ['desktop', 'mobile'] as const;
export type PbbEducationViewport = typeof PBB_EDUCATION_VIEWPORTS[number];

/**
 * Stable keys for the PBB signup FAQ accordion items, in displayed order. Deliberately
 * self-describing so analysts can read them without a mapping table; rename only with
 * an analyst heads-up.
 */
export const PBB_EDUCATION_FAQ_KEYS = [
  'what_is_pay_by_bank',
  'is_pay_by_bank_safe',
  'does_retailer_see_bank_login',
  'forced_to_use_pay_by_bank',
  'when_money_removed_from_account',
  'payment_issue_contact',
] as const;
export type PbbEducationFaqKey = typeof PBB_EDUCATION_FAQ_KEYS[number];

export const PBB_EDUCATION_FAQ_ACTIONS = ['expanded', 'collapsed'] as const;
export type PbbEducationFaqAction = typeof PBB_EDUCATION_FAQ_ACTIONS[number];

export const PBB_EDUCATION_CAROUSEL_DIRECTIONS = ['next', 'back'] as const;
export type PbbEducationCarouselDirection = typeof PBB_EDUCATION_CAROUSEL_DIRECTIONS[number];

/**
 * Surfaces that can fire walletPass events. Adding a new surface here is a deliberate
 * change so the analyst tracking these events can be told about it.
 */
export const WALLET_PASS_SURFACES = ['postSignup'] as const;
export type WalletPassSurface = typeof WALLET_PASS_SURFACES[number];

/**
 * Surfaces that can fire loyaltyEnrollmentAction events. Adding a new surface here is a
 * deliberate change so the analyst tracking these events can be told about it.
 */
export const LOYALTY_ENROLLMENT_SURFACES = [
  'autoPopup',
  'orderStatusCta',
  'instoreEnrollment',
  'accountPanel',
  'referralsPanel',
  'irlEvent',
  'enterpriseSignup',
  'kioskPro',
] as const;
export type LoyaltyEnrollmentSurface = typeof LOYALTY_ENROLLMENT_SURFACES[number];

/**
 * Actions on the loyalty enrollment flows used in tracking events.
 */
export const LOYALTY_ENROLLMENT_ACTIONS = ['viewed', 'submitted', 'succeeded', 'failed'] as const;
export type LoyaltyEnrollmentAction = typeof LOYALTY_ENROLLMENT_ACTIONS[number];

export const ACCOUNT_SIGNUP_ACTIONS = ['viewed', 'submitted', 'succeeded', 'failed'] as const;
export type AccountSignupAction = typeof ACCOUNT_SIGNUP_ACTIONS[number];

export const ACCOUNT_SIGNUP_METHODS = ['emailPassword', 'emailCode', 'google', 'apple', 'guestConversion'] as const;
export type AccountSignupMethod = typeof ACCOUNT_SIGNUP_METHODS[number];

export const ACCOUNT_SIGNUP_SURFACES = [
  'signupModal',
  'emailCode',
  'socialOAuth',
  'guestCheckout',
  'kioskCheckout',
  'enterpriseSignup',
  'instoreEnrollment',
  'irlEvent',
] as const;
export type AccountSignupSurface = typeof ACCOUNT_SIGNUP_SURFACES[number];

/**
 * Loyalty integrations that can be enrolled into. Excludes Integrations.none, which means
 * "no loyalty provider" and can never be the target of an enrollment event.
 */
export const LOYALTY_ENROLLMENT_INTEGRATIONS = [
  Integrations.alpineiq,
  Integrations.springbig,
  Integrations.dutchiepos,
] as const;
export type LoyaltyEnrollmentIntegration = typeof LOYALTY_ENROLLMENT_INTEGRATIONS[number];

/**
 * Surfaces that can fire phoneVerificationNudgeAction events. Adding a new surface here is a
 * deliberate change so the analyst tracking these events can be told about it.
 */
export const PHONE_VERIFICATION_NUDGE_SURFACES = [
  'postLoginModal',
  'myAccountBanner',
  'myAccountOtpModal',
  'login',
  // FINTECH-4845: the net-new, flag-gated self-serve Verify button next to the Mobile Phone
  // field -- deliberately its own surface, never folded into 'myAccountBanner', so the two
  // entry points' funnels stay analytically distinguishable.
  'myAccountSelfServeButton',
] as const;
export type PhoneVerificationNudgeSurface = typeof PHONE_VERIFICATION_NUDGE_SURFACES[number];

/**
 * Stages of the phone-verification Nudge funnel. One event type with an action enum, per
 * `app/services/telemetry/README.md`'s workflow-stage guidance, rather than a separate event
 * type per stage -- see `use-phone-verification-nudge-analytics.ts` for the full rationale.
 */
export const PHONE_VERIFICATION_NUDGE_ACTIONS = [
  'intent_marker_set',
  'intent_marker_consumed',
  'suppressed',
  'shown',
  'verify_clicked',
  'code_sent',
  'otp_submitted',
  'otp_resent',
  'change_number_submitted',
  'completed',
  'dismissed',
  'login_other_account',
  'failed',
] as const;
export type PhoneVerificationNudgeActionType = typeof PHONE_VERIFICATION_NUDGE_ACTIONS[number];

/**
 * consumerPulseGoogleReviewAction vocabulary: whether the customer took the Google review offer
 * ('clicked') or turned it down ('declined'), and which entry point the survey came from. Mirrors
 * the Armageddon `ConsumerPulseGoogleReviewAction` schema.
 */
export const CONSUMER_PULSE_GOOGLE_REVIEW_ACTIONS = ['clicked', 'declined'] as const;
export type ConsumerPulseGoogleReviewActionType = typeof CONSUMER_PULSE_GOOGLE_REVIEW_ACTIONS[number];
export const CONSUMER_PULSE_SOURCE_TYPES = ['web-checkout', 'email', 'qr-code'] as const;
export type ConsumerPulseSourceType = typeof CONSUMER_PULSE_SOURCE_TYPES[number];

/**
 * `NudgeLeg` minus `'none'` -- events never carry `'none'` (the caller doesn't open the modal
 * at all in that case). The runtime array mirrors `NudgeLeg`'s members rather than restating
 * unrelated strings; it exists only because the yup schema needs a literal array to validate
 * against, and TypeScript unions have no runtime representation to derive one from.
 */
export const PHONE_VERIFICATION_NUDGE_LEGS = ['leg_one', 'leg_two', 'add_phone'] as const;
export type PhoneVerificationNudgeLeg = Exclude<NudgeLeg, 'none'>;

/** Objects used in events */
export type AnalyticsCart = { subtotal?: number; items?: any; checkoutToken?: string } & (Orders | OrdersCreateInput);
export type AnalyticsProduct = OrderProduct | Product | Products;
export type AnalyticsOrder = Orders & { cart?: any };
export type AnalyticsDispensary = Dispensaries & { isDutchiePayEnabledForDispo?: boolean };
export type AnalyticsUser = { id?: string; isLoggedIn?: boolean; isEnrolledDutchiePay?: boolean };
export type AnalyticsMenuVariant = InterfaceVariants;
export type AnalyticsNavigationBreadcrumbs = string;
export type AnalyticsPromotion = {
  product: Product;
  campaignId?: string;
  analyticsPage?: string;
  trackerSource: string;
  position?: number;
};

type StoreRoutingEvent = {
  source: StoreRoutingSource;
  originDispensaryId: string;
  destinationDispensaryId?: string;
  cartHadItems?: boolean;
} & (
  | { action: 'failed'; failureReason: StoreRoutingFailureReason }
  | { action: Exclude<StoreRoutingAction, 'failed'>; failureReason?: never }
);

/**
 * Payloads of the events that we track.
 */
export type Events = {
  /* FLOW(track-an-event).STEP_1: add your event + payload here */
  storeRouting: StoreRoutingEvent;

  accountLogin: {
    newAccount: boolean;
    customerId: string;
    dispensary: {
      id: string;
      name: string;
    };
    analyticsEventLabel?: string;
  };
  addedProductToCart: {
    quantity: number;
    product: AnalyticsProduct;
    option?: string | null;
    trackerSource?: string | null;
    position?: number;
  };
  buyItAgainImpression: Omit<AnalyticsPromotion, 'product'> & {
    products: Product[];
    trackerSource?: string | null;
  };
  buyItAgainViewAllClicked: {
    destinationUrl: string;
    trackerSource?: string | null;
  };
  loyaltySignup: {
    analyticsEventLabel: string;
  };
  placedOrder: {
    checkoutToken: string;
    order: AnalyticsOrder;
    isDutchiePayEnabledForDispo: boolean;
    isEnrolledDutchiePay: boolean;
    roundupForCharityAnalyticsData?: {
      roundUpTheChangeEligible: boolean;
      roundUpTheChangeFeeDonationId: number;
      roundUpTheChangeSelected: boolean;
      roundUpTheChangeAmount: number;
    };
  };
  removedProductFromCart: { quantity: number; product: AnalyticsProduct };
  searchedForProduct: { query: string };
  searchResults: {
    query: string;
    hasResults: boolean;
    products?: AnalyticsProduct[];
  };
  checkoutStarted: {
    total: number;
    revenue: number;
    tax: number;
    discount?: number;
    currency: string;
    products: AnalyticsProduct[];
  };
  viewedCheckout: {
    checkoutToken: string;
    dispensaryId: string;
    dispensaryName: string;
    isDutchiePayEnabledForDispo: boolean;
    isEnrolledDutchiePay: boolean;
    tax?: number;
  };
  checkoutSession: {
    checkoutToken: string;
    dispensaryId: string;
    dispensaryName: string;
    isDutchiePayEnabledForDispo: boolean;
    isEnrolledDutchiePay: boolean;
    customerId: string;
    loggedIn: boolean;
    tax?: number;
  };
  viewedProduct: { product: AnalyticsProduct; trackerSource?: string | null };
  productClicked: {
    position: number;
    product: AnalyticsProduct;
    trackerSource?: string | null;
    placement?: ProductPlacement;
    provider?: ProductsProvider;
  };
  cartUpdated: {
    cartId?: string;
    products?: AnalyticsProduct[];
    dispensaryId?: string;
    dispensaryName?: string;
    deliveryMethod?: string;
    paymentType?: string;
    purchaseType?: string;
    isScheduled?: boolean;
  };
  cartViewed: {
    cartId: string;
    products: AnalyticsProduct[];
    dispensaryId: string;
    dispensaryName: string;
  };
  imageBannerClicked: {
    dispensaryId: string;
    dispensaryName: string;
  };
  // DutchiePay Enrollment
  paymentsError: { reasonCode: string; meta?: any; surface?: 'kiosk' | 'mobile' | 'web'; enhancedMessages?: boolean };
  dutchiePayKioskQRCodeModalViewed: {
    checkoutToken: string;
  };
  viewedKioskCheckoutModal: Record<string, never>;
  dutchiePayEnrollmentButtonClicked: {
    dutchiePaySessionToken: string;
    buttonLocation: SignupButtonLocation;
  };
  dutchiePayCTAButtonClicked: { dutchiePaySessionToken: string };
  dutchiePayRelinkButtonViewed: Record<string, never>;
  dutchiePayRelinkButtonClicked: Record<string, never>;
  dutchiePayUnlinkRemoveCtaClicked: Record<string, never>;
  dutchiePayUnlinkModalViewed: Record<string, never>;
  dutchiePayUnlinkConfirmed: Record<string, never>;
  dutchiePayUnlinkCancelled: Record<string, never>;
  dutchiePayUnlinkSuccess: Record<string, never>;
  dutchiePayUnlinkError: Record<string, never>;
  dutchiePayUnenrollment: {
    action: 'failed' | 'succeeded';
    changedFields: PbbIdentityChangedField[];
    reason: PbbUnenrollmentReason;
    surface?: PbbUnenrollmentSurface;
  };
  dutchiePayEnrollmentStep: {
    dutchiePaySessionToken: string;
    step: EnrollmentSteps;
    action: EVENT_ACTIONS;
    enrollmentSource?: string;
    context?: EnrollmentContexts;
    // Per-CTA attribution + anonymous visitor id carried in from partner-theme
    // landing pages (e.g. Ascend's whitelabel Pay by Bank landing) via query
    // params on the /pay/signup link — see `ctaPlacement`/`pbbVisitorId` reads
    // in useDutchiePayAnalytics.
    ctaPlacement?: string;
    pbbVisitorId?: string;
  };
  dutchiePayEnrollmentStepCancelled: {
    step: EnrollmentSteps;
  };
  dutchiePayPlaidOnEvent: {
    dutchiePaySessionToken: string;
    // Plaid emits stable events plus additional string event names (e.g. TRANSITION_VIEW)
    // that are not part of the PlaidLinkStableEvent enum.
    eventName: PlaidLinkStableEvent | 'TRANSITION_VIEW';
    eventMetadata: Partial<PlaidLinkOnEventMetadata>;
    connectionMethod: BankConnectionContexts;
  };
  dutchiePayPlaidOnLoad: {
    dutchiePaySessionToken: string;
    phonePassedToPlaid?: boolean | null;
    connectionMethod: BankConnectionContexts;
  };
  dutchiePayIncentiveExposure: {
    dutchiePaySessionToken: string;
    userType: string;
    incentiveValue?: string;
    isCashBack?: boolean;
    source?: string;
    // Disambiguates points boost from the signup discount — both report isCashBack: false.
    rewardType?: 'cash_back' | 'points_boost';
  };
  dutchiePaySignupEducationSectionViewed: {
    dutchiePaySessionToken: string;
    section: PbbEducationSection;
    viewport: PbbEducationViewport;
  };
  dutchiePaySignupEducationFaqToggled: {
    dutchiePaySessionToken: string;
    faqKey: PbbEducationFaqKey;
    action: PbbEducationFaqAction;
    viewport: PbbEducationViewport;
  };
  dutchiePaySignupEducationCarouselNavigated: {
    dutchiePaySessionToken: string;
    direction: PbbEducationCarouselDirection;
    fromSection: PbbEducationSection;
    toSection: PbbEducationSection;
  };
  dutchiePayServiceFeeShown: {
    dutchiePaySessionToken: string;
    feesDisabled: boolean;
    onlinePaymentMethod: string;
    onlineFeeLabel: string;
    instoreFeeLabel: string;
  };
  dutchiePayPaymentMethodSelected: {
    dutchiePaySessionToken: string;
    paymentMethod: string;
    pbbOffered: boolean;
    isPbb: boolean;
  };
  dutchiePayPlaidOnSuccess: {
    dutchiePaySessionToken: string;
    eventMetadata: PlaidLinkOnSuccessMetadata;
    connectionMethod: BankConnectionContexts;
  };
  dutchiePayPlaidOnExit: {
    dutchiePaySessionToken: string;
    plaidError: PlaidLinkError | null;
    eventMetadata: PlaidLinkOnExitMetadata;
    connectionMethod: BankConnectionContexts;
  };
  dutchiePayAeroSyncOnEvent: {
    dutchiePaySessionToken: string;
    eventName: string;
    eventMetadata: WidgetEventPayloadType;
  };
  dutchiePayAeroSyncOnError: {
    dutchiePaySessionToken: string;
    error: string;
  };
  dutchiePayAeroSyncOnLoad: {
    dutchiePaySessionToken: string;
  };
  dutchiePayAeroSyncOnClose: {
    dutchiePaySessionToken: string;
  };
  dutchiePayAeroSyncOnSuccess: {
    dutchiePaySessionToken: string;
    connectionId: string;
    clientName: string;
    aeroPassUserUuid: string;
  };
  dutchiePayPreAuthEvent: {
    dutchiePaySessionToken: string;
    eventName: AeroSyncPreAuthEvent;
    eventMetadata: AeroSyncPreAuthEventMetadata | undefined;
  };
  // DuthchiePay Instore
  dutchiePayInstoreAccountLinkViewed: {
    instoreCartToken: string;
    showBarcode: boolean;
  };
  dutchiePayInstoreLoginViewed: { instoreCartToken: string; loginStep: string };
  dutchiePayInstoreCartViewed: {
    instoreCartId: string;
    instoreCartToken: string;
    dispensary: {
      id: string;
      name: string;
    };
  };
  dutchiePayInstoreCartApprovalClicked: {
    instoreCartId: string;
    instoreCartToken: string;
    dispensary: {
      id: string;
      name: string;
    };
    checkoutFlow?: CheckoutFlow;
  };
  dutchiePayInstoreCartApprovalSucceeded: {
    instoreCartId: string;
    instoreCartToken: string;
    dispensary: {
      id: string;
      name: string;
    };
    checkoutFlow?: CheckoutFlow;
  };
  dutchiePayInstoreCartApprovalFailed: {
    instoreCartId: string;
    instoreCartToken: string;
    dispensary: {
      id: string;
      name: string;
    };
    checkoutFlow?: CheckoutFlow;
  };
  sponsoredProductImpression: AnalyticsPromotion;
  sponsoredProductClicked: AnalyticsPromotion;
  sponsoredBannerImpression: Omit<AnalyticsPromotion, 'product'> & {
    products: Product[];
    impressionUrl: string;
  };
  sponsoredBannerClicked: Omit<AnalyticsPromotion, 'product'> & {
    products: Product[];
    clickUrl: string;
  };
  privacyPreferenceUpdated: {
    preference: string;
    action: string;
    region: string;
  };
  gaGTMClickEvent: {
    eventCategory: string;
    eventAction: string;
    eventLabel?: string;
  };
  dispensaryClicked: {
    dispensaryId: string;
    dispensaryName: string;
    dispensaryAddress?: string;
    dispensaryChainName?: string;
    source: string;
    meta?: Record<string, any>;
  };
  loginNudge: {
    action: 'clicked' | 'dismissed' | 'viewed';
    variant?: string;
    dutchiePaySessionToken?: string;
  };
  loginGateShown: {
    dutchiePaySessionToken: string;
    source: string;
    gateVariant?: string;
  };
  loginEntryPointClicked: {
    dutchiePaySessionToken: string;
    source: string;
  };
  dutchiePayCheckoutFlowAction: {
    dutchiePaySessionToken: string;
    action: CheckoutFlowActionType;
    value?: string;
  };
  roundupForCharityViewed: Record<string, never>;
  roundupForCharityToggled: {
    action: 'disabled' | 'enabled';
  };
  walletPass: {
    action: 'clicked' | 'dismissed' | 'viewed';
    type: 'button' | 'qrCode';
    surface?: WalletPassSurface;
  };
  dutchiePayLinkToPayInitiated: {
    source: 'email_cta' | 'sms_cta';
    orderId: string;
    messageStage?: string;
    entryPoint: 'direct' | 'post_signup';
  };
  dutchiePayLinkToPayIneligible: {
    source: 'email_cta' | 'sms_cta';
    orderId: string;
    reason: string;
    messageStage?: string;
  };

  // `surface` carries the shared checkout-experience tag so these line up with the charge
  // record; `customerState` is omitted for guests (order history is unavailable then).
  dutchiePayEcommModalPaymentSwitchViewed: {
    orderId: string;
    ctaVariant: 'enrolled' | 'new' | 'relink';
    surface: string;
    customerState?: 'new' | 'returning';
    checkoutFlow: CheckoutFlow;
  };
  dutchiePayEcommModalPaymentSwitchClicked: {
    orderId: string;
    ctaVariant: 'enrolled' | 'new' | 'relink';
    surface: string;
    customerState?: 'new' | 'returning';
    checkoutFlow: CheckoutFlow;
  };
  dutchiePayEcommModalPaymentSwitchOutcome: {
    orderId: string;
    ctaVariant: 'enrolled' | 'new' | 'relink';
    surface: string;
    customerState?: 'new' | 'returning';
    outcome: 'failed' | 'success';
    failureReason?: string;
    checkoutFlow: CheckoutFlow;
  };

  // Loyalty CTA on the Order Confirmation Modal v2. `surface` reuses the same modal
  // checkout-experience tag as the payment-switch events; `customerState` is omitted for guests.
  dutchiePayLoyaltyCtaClicked: {
    orderId: string;
    surface: string;
    customerState?: 'new' | 'returning';
  };

  // Instore Enrollment Analytics
  instoreEnrollmentStepViewed: {
    step: InstoreEnrollmentStep;
  };
  instoreEnrollmentStepCompleted: {
    step: InstoreEnrollmentStep;
  };
  instoreEnrollmentStepSkipped: {
    step: InstoreEnrollmentStep;
  };
  medCardSignupCompleted: {
    waitingAtDispensary: boolean;
  };
  medCardSignupFailed: {
    errorType: string;
    errorMessage: string;
  };

  // Post-signup onboarding modals (loyalty join, bank link, loyalty pass)
  onboardingModalStep: {
    modal: OnboardingModalName;
    action: OnboardingModalAction;
    source?: OnboardingModalSource;
    dutchiePaySessionToken?: string;
    flowPosition?: number;
    errorCode?: string;
    errorMessage?: string;
  };

  accountSignupAction: {
    action: AccountSignupAction;
    method: AccountSignupMethod;
    surface: AccountSignupSurface;
    dutchiePaySessionToken?: string;
    errorCode?: string;
    errorMessage?: string;
  };

  // Fires from loyalty sign-up flows across the app, tagged by surface and loyalty integration.
  loyaltyEnrollmentAction: {
    surface: LoyaltyEnrollmentSurface;
    integration: LoyaltyEnrollmentIntegration;
    action: LoyaltyEnrollmentAction;
    errorCode?: string;
    errorMessage?: string;
  };

  // Fires from the phone-verification Nudge funnel: the post-login modal, the My Account
  // banner + its OTP host, and the login-intent marker that gates the modal.
  phoneVerificationNudgeAction: {
    action: PhoneVerificationNudgeActionType;
    surface: PhoneVerificationNudgeSurface;
    leg?: PhoneVerificationNudgeLeg;
    errorCode?: string;
  };

  // Fires when a customer answers the first-party survey's Google review offer. `rating` is the
  // 1-5 score they just submitted; no free text or customer identity is sent.
  consumerPulseGoogleReviewAction: {
    action: ConsumerPulseGoogleReviewActionType;
    sourceType: ConsumerPulseSourceType;
    rating: number;
  };

  // Fires once when the post-signup onboarding sequence starts, so a missing per-modal event
  // can be distinguished from ineligibility vs. the user bailing before reaching it.
  onboardingFlowStarted: {
    source: OnboardingModalSource;
    dutchiePaySessionToken?: string;
    eligibleSteps: OnboardingModalName[];
    conditionalSteps?: OnboardingModalName[];
  };

  // Product Reviews & Ratings. View events fire once per product per page view;
  // `source` distinguishes the review page's entry points (email link vs order history).
  productRatingViewed: {
    productId: string;
    avgRating?: number | null;
    reviewCount: number;
  };
  productReviewsViewed: {
    productId: string;
    reviewCount: number;
    avgRating?: number | null;
    hasAiSummary: boolean;
  };
  productReviewAiSummaryViewed: {
    productId: string;
  };
  productReviewStatusChecked: {
    productCount: number;
    reviewedCount: number;
  };
  productReviewCtaClicked: {
    orderId: string;
    productId: string;
    source: 'order_history';
  };
  productReviewSubmitted: {
    productId: string;
    rating: number;
    effectsCount: number;
    hasBody: boolean;
    source?: 'email' | 'order_history';
  };

  // Concierge telemetry — payload shapes are owned by the concierge bridge contract
  conciergeEntryPointClicked: ConciergeTelemetryEvents['conciergeEntryPointClicked'];
  conciergeImpression: ConciergeTelemetryEvents['conciergeImpression'];
  conciergeAction: ConciergeTelemetryEvents['conciergeAction'];
  conciergeMessageSent: ConciergeTelemetryEvents['conciergeMessageSent'];
  conciergeExited: ConciergeTelemetryEvents['conciergeExited'];
  conciergeBundleAdded: ConciergeTelemetryEvents['conciergeBundleAdded'];
  conciergeAccessGate: ConciergeTelemetryEvents['conciergeAccessGate'];
  conciergeConversationRefreshed: ConciergeTelemetryEvents['conciergeConversationRefreshed'];
  conciergeMessageStopped: ConciergeTelemetryEvents['conciergeMessageStopped'];
  conciergeError: ConciergeTelemetryEvents['conciergeError'];
  conciergeIdleWarning: ConciergeTelemetryEvents['conciergeIdleWarning'];
  conciergeSessionLimitReached: ConciergeTelemetryEvents['conciergeSessionLimitReached'];
};

/**
 * Context available to all events
 */
export type AnalyticsContext = {
  activeCart: AnalyticsCart | null;
  activeOrder: AnalyticsOrder | null;
  activeDispensary: AnalyticsDispensary | null;
  activeUser: AnalyticsUser | null;
  activePath: { normalized: string; concrete: string } | null;
  activeMenuVariant: AnalyticsMenuVariant | null;
  activeProductPosition: number | null;
  activeNavigationBreadcrumbs: AnalyticsNavigationBreadcrumbs | null;
  activeIsUserEnrolledDutchiePay: boolean | null;
  activePromotion: AnalyticsPromotion | null;
  cartDiscountSummary: ReceiptDiscountSummary | null;
  checkoutToken: string | null;
  dutchiePaySessionToken: string | null;
  featureFlags: AnalyticsFeatureFlags;
  isKioskOrder: boolean | null;
  isHubOrder: boolean | null;
  ldClient: LDClient | undefined;
};

/**
 * The interface that each Tracker must implement.
 *
 * Note: this is just an object with a payload-handling method for each event in Events. It should never need to be
 *       altered since it's based on the information in {@link Events}.
 */
export type EventTracker = {
  [Property in keyof Events]: (payload: Events[Property], context: AnalyticsContext) => void;
} & {
  onContextChange?: (
    update: Partial<AnalyticsContext>,
    newContext: AnalyticsContext,
    oldContext: AnalyticsContext
  ) => void;
};
